import React from "react";
//#region types
import { TodoCardProps } from "models/propTypes/todoPropTypes";
import { TranslationKeys } from "translations";
import { WintSuperkollAPIModel as ApiModel } from "wint-api-client/dist/types";
import { ImageMeta } from "models/propTypes/fileViewerPropTypes";
import { FormHandles } from "models/propTypes/genericFormProps";
import { CommentsFormTypes } from "models/formTypes";
import { FormFieldConstants } from "utils/constants";
import { UploadedFile } from "models/utilTypes/fileUtilsTypes";
//#endregion
//#region components
import TopTodoCard from "../../_partials/topTodoCard";
import {
  FileViewerContainer,
  GenericCommentsView,
  TitledContainerInner,
  EntityCardContent,
  Skeletons,
} from "components/common";
import { CommentsViewWithInputForm } from "components/common/comments";
import {
  SupplementaryMaterialTodoFooter,
  useOnSupplementaryMaterialViewInfo,
  useSupplementaryMaterialSnoozeAction,
} from "components/supplementaryMaterial";
import { useOnViewInfo as useIncomingInvoiceViewInfo } from "components/incomingInvoice";
import {
  useIncomingInvoiceService,
  useReceiptService,
  useFileUploadService,
  useSupplementaryMaterialService,
} from "serviceHooks";
//#endregion
import { LocalizationHelperInstance } from "utils/helpers";

const SupplementaryMaterialTodo: React.FC<TodoCardProps> = (props) => {
  const incomingInvoiceService = useIncomingInvoiceService();
  const receiptService = useReceiptService();
  const fileUploadService = useFileUploadService();
  const supplementaryMaterialService = useSupplementaryMaterialService();

  const supplementaryMaterial = props.item.supplementaryMaterials;
  const formRef = React.useRef<FormHandles>(null);

  const [invoice, setInvoice] = React.useState<ApiModel.IncomingInvoice>();
  const [receipt, setReceipt] = React.useState<ApiModel.Receipt>();
  const [attachments, setAttachments] = React.useState<ImageMeta[]>();
  const snoozeActions = useSupplementaryMaterialSnoozeAction(supplementaryMaterial!.id, props.onActionSuccess);

  React.useEffect(() => {
    if (!supplementaryMaterial || !supplementaryMaterial.sourceId) return;

    if (supplementaryMaterial.sourceType === ApiModel.SupplementaryMaterialSourceType.IncomingInvoice) {
      incomingInvoiceService.get(supplementaryMaterial.sourceId).then((data) => {
        const invoice = data.data;
        setInvoice(invoice);
        setAttachments(
          invoice.attachments?.map(
            (attachment): ImageMeta => ({
              ...attachment,
              numberOfPages: 1,
            })
          ) ?? []
        );

        invoice.attachments?.forEach((attachment) => {
          incomingInvoiceService.getAttachment(invoice.id, attachment.id).then((res) => {
            loadAttachmentContent(attachment.id, res);
          });
        });
      });
    } else {
      receiptService.get(supplementaryMaterial.sourceId).then((data) => {
        const receipt = data;
        setReceipt(receipt);
        setAttachments(
          receipt.attachments?.map(
            (attachment): ImageMeta => ({
              ...attachment,
              numberOfPages: 1,
            })
          ) ?? []
        );

        receipt.attachments?.forEach((attachment) => {
          receiptService.getAttachmentData(receipt.id, attachment.id).then((res) => {
            loadAttachmentContent(attachment.id, res);
          });
        });
      });
    }
  }, [supplementaryMaterial?.sourceId]);

  const loadAttachmentContent = (id: number, data: string) => {
    setAttachments((prevAttachments) => {
      if (prevAttachments) {
        const newAttachments = prevAttachments.map((p) => ({ ...p }));
        const attachmentToUpdate = newAttachments.find((a) => a.id === id);
        if (attachmentToUpdate) {
          attachmentToUpdate.data = data;
        }
        return newAttachments;
      } else return prevAttachments;
    });

    if (formRef.current && formRef.current.getValues && formRef.current.setFieldValue) {
      const values: CommentsFormTypes.CommentWithFile = formRef.current.getValues();
      const newFiles = [...(values.filesToUpload ?? [])];
      const file = newFiles.find((x) => x.id === id);
      if (file) {
        file.data = data;
        formRef.current.setFieldValue(FormFieldConstants.Comments.CommentWithFile.FilesToUpload, newFiles);
      }
    }
  };

  const onInvoiceViewInfoClick = useIncomingInvoiceViewInfo(invoice?.id ?? 0, formRef);
  const onReceiptViewInfoClick = useOnSupplementaryMaterialViewInfo(receipt?.id ?? 0, formRef);

  // An invoice still in Ohanterat carries unverified OCR readings, so the to do offers only the
  // supplier, the amount and the document until it has been handled.
  const onViewAllClick =
    supplementaryMaterial?.sourceType === ApiModel.SupplementaryMaterialSourceType.IncomingInvoice && invoice
      ? invoice.state === ApiModel.IncomingInvoiceState.Unhandled
        ? undefined
        : onInvoiceViewInfoClick
      : supplementaryMaterial?.sourceType === ApiModel.SupplementaryMaterialSourceType.CustomerReceipt && receipt
        ? onReceiptViewInfoClick
        : undefined;

  const onTodoSubmit = () => {
    const onSubmit = async (values: CommentsFormTypes.CommentWithFile): Promise<any> => {
      const files = await fileUploadService.updateMissingAttachments(
        values.filesToUpload.filter((m) => !m.id) ?? [],
        receipt
          ? receiptService.getAttachmentData.bind(null, receipt!.id)
          : incomingInvoiceService.getAttachment.bind(null, invoice!.id)
      );

      return supplementaryMaterialService.submitSupplementaryMaterial(
        supplementaryMaterial!.id,
        values.explanation,
        files
      );
    };

    return (formRef.current && formRef.current.submit(onSubmit)) || new Promise<void>((resolve) => resolve());
  };

  const getCommentType = () => {
    switch (supplementaryMaterial?.sourceType) {
      case ApiModel.SupplementaryMaterialSourceType.IncomingInvoice:
        return ApiModel.GenericCommentType.IncomingInvoice;
      case ApiModel.SupplementaryMaterialSourceType.CustomerReceipt:
        return ApiModel.GenericCommentType.Receipt;
      default:
        return ApiModel.GenericCommentType.None;
    }
  };

  const getDescriptionHeadingOne = () => {
    if (invoice) {
      return LocalizationHelperInstance.getFormattedCurrency(invoice?.amountWithSign ?? 0, invoice?.currency);
    } else if (receipt) {
      return LocalizationHelperInstance.getFormattedCurrency(receipt?.amount ?? 0, receipt?.currency);
    } else {
      return <Skeletons.TopCardDescriptionOneSkeleton />;
    }
  };

  const getDescriptionHeadingTwo = () => {
    if (invoice) {
      return invoice.supplier?.name ?? "";
    } else if (receipt) {
      return `${LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.SubmittedBy)}: ${
        receipt?.createdByPersonName
      }`;
    }
  };

  const getDescriptionHeadingThree = () => {
    return `${LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.ApprovedBy)}:${
      receipt ? receipt.createdByPersonName : invoice ? (invoice?.certifyPersonName ?? "") : ""
    }`;
  };

  const initialAttachments = React.useMemo(
    () =>
      (supplementaryMaterial?.sourceType === ApiModel.SupplementaryMaterialSourceType.CustomerReceipt
        ? receipt?.attachments
        : invoice?.attachments
      )?.map(
        (attachment): UploadedFile => ({
          ...attachment,
          contentType: attachment.contentType ?? "",
          fileName: attachment.fileName ?? "",
          id: attachment.id,
          fileSize: 0,
          data: attachment.data ?? "",
        })
      ),
    [receipt, invoice, supplementaryMaterial]
  );

  const onAttachmentsChange = (name: string, updatedValue: UploadedFile[]) => {
    setAttachments(
      updatedValue.map<ImageMeta>((upload) => ({
        ...upload,
        id: upload.id as number,
        numberOfPages: 1,
      }))
    );
  };

  return (
    <EntityCardContent
      topTodoCard={
        <TopTodoCard
          item={props.item}
          descriptionHeadingOne={getDescriptionHeadingOne()}
          descriptionHeadingTwo={getDescriptionHeadingTwo()}
          descriptionheadingThree={getDescriptionHeadingThree()}
          footerText={`${LocalizationHelperInstance.getFormattedMessage(
            supplementaryMaterial?.sourceType === ApiModel.SupplementaryMaterialSourceType.CustomerReceipt
              ? TranslationKeys.Todos.TodoCardSubTypeText_ReceiptSentBack
              : TranslationKeys.Todos.TodoCardSubTypeText_IncomingInvoiceSentBack
          )}: ${
            supplementaryMaterial?.updated
              ? LocalizationHelperInstance.getFormattedDate(supplementaryMaterial.updated.toString())
              : "-"
          }`}
          quickActions={[snoozeActions]}
          onViewAllClick={onViewAllClick}
        />
      }
      todoMainSection={
        <TitledContainerInner
          header={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.SupplementaryMaterial.CommentHeader)}
          subHeader={LocalizationHelperInstance.getFormattedMessage(
            TranslationKeys.SupplementaryMaterial.CommentSubHeader
          )}
        >
          <CommentsViewWithInputForm
            ref={formRef}
            parentId={receipt?.id ?? invoice?.id ?? 0}
            commentType={getCommentType()}
            initialValues={{
              filesToUpload: initialAttachments,
            }}
            onUploadedFilesChangeHandler={onAttachmentsChange}
            commentOrFileRequired
          />
        </TitledContainerInner>
      }
      infoViewer={
        attachments !== undefined && attachments.length > 0 ? (
          <FileViewerContainer meta={attachments ?? []} loading={attachments === undefined} />
        ) : undefined
      }
      footer={
        <SupplementaryMaterialTodoFooter
          supplementaryMaterial={supplementaryMaterial!}
          onTodoClear={onTodoSubmit}
          markAsDoneHandler={props.onActionSuccess}
        />
      }
    >
      <GenericCommentsView parentId={supplementaryMaterial?.sourceId ?? 0} type={getCommentType()} collapsed />
    </EntityCardContent>
  );
};

export default SupplementaryMaterialTodo;
